import React from 'react';

const STATUS_CONFIGS = [
    { key: 'Backlog', label: 'Backlog / Upcoming', color: '#2563eb', bgClass: 'bg-blue-600' },
    { key: 'In Progress', label: 'In Progress', color: '#22c55e', bgClass: 'bg-green-500' },
    { key: 'Under Review', label: 'Under Review', color: '#f59e0b', bgClass: 'bg-amber-500' },
    { key: 'On Hold', label: 'On Hold', color: '#f43f5e', bgClass: 'bg-rose-500' },
    { key: 'Completed', label: 'Completed', color: '#94a3b8', bgClass: 'bg-slate-400' },
];

const CIRCUMFERENCE = 2 * Math.PI * 60; // ~376.9911

export default function TasksDonutChart({
    statusCounts = {},
    selectedStatus = null,
    onStatusSelect,
}) {
    // 5 statuses
    const backlogCount = statusCounts['Backlog'] || 0;
    const inProgressCount = statusCounts['In Progress'] || 0;
    const underReviewCount = statusCounts['Under Review'] || 0;
    const onHoldCount = statusCounts['On Hold'] || 0;
    const completedCount = statusCounts['Completed'] || 0;

    const totalCount = backlogCount + inProgressCount + underReviewCount + onHoldCount + completedCount;

    // Calculate segments for the SVG donut
    const countsMap = {
        'Backlog': backlogCount,
        'In Progress': inProgressCount,
        'Under Review': underReviewCount,
        'On Hold': onHoldCount,
        'Completed': completedCount,
    };

    let accumulatedOffset = 0;
    const segments = STATUS_CONFIGS.map((status) => {
        const count = countsMap[status.key] || 0;
        const ratio = totalCount > 0 ? count / totalCount : 0;
        const dashLength = ratio * CIRCUMFERENCE;
        const dashOffset = -accumulatedOffset;
        accumulatedOffset += dashLength;

        return {
            ...status,
            count,
            ratio,
            dashLength,
            dashOffset,
        };
    });

    const handleRowClick = (statusKey) => {
        if (!onStatusSelect) return;
        // Toggle filter if clicked again
        if (selectedStatus === statusKey) {
            onStatusSelect(null);
        } else {
            onStatusSelect(statusKey);
        }
    };

    return (
        <div className="flex flex-col pr-0 lg:pr-2">
            <div className="mb-5">
                <h3 className="text-base font-bold text-slate-900 dark:text-white font-headline tracking-tight">
                    Tasks
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    Distribution of your tasks by status.
                </p>
            </div>

            <div className="flex items-center justify-between gap-4 mt-auto mb-auto">
                {/* Donut Chart SVG */}
                <div className="relative w-32 h-32 flex-shrink-0 flex items-center justify-center">
                    <svg className="w-full h-full -rotate-90" viewBox="0 0 160 160">
                        {/* Background track circle */}
                        <circle
                            cx="80"
                            cy="80"
                            r="60"
                            fill="transparent"
                            stroke="#f1f5f9"
                            className="dark:stroke-slate-800"
                            strokeWidth="18"
                        />

                        {/* Segment circles */}
                        {totalCount > 0 &&
                            segments.map((seg) => {
                                if (seg.count === 0) return null;
                                const isSelected = selectedStatus === seg.key;
                                return (
                                    <circle
                                        key={seg.key}
                                        cx="80"
                                        cy="80"
                                        r="60"
                                        fill="transparent"
                                        stroke={seg.color}
                                        strokeWidth={isSelected ? 22 : 18}
                                        strokeDasharray={`${seg.dashLength} ${CIRCUMFERENCE}`}
                                        strokeDashoffset={seg.dashOffset}
                                        className="transition-all duration-300 cursor-pointer hover:opacity-80"
                                        onClick={() => handleRowClick(seg.key)}
                                        title={`${seg.label}: ${seg.count}`}
                                    />
                                );
                            })}
                    </svg>

                    {/* Donut Center Count */}
                    <div className="absolute flex flex-col items-center justify-center text-center pointer-events-none">
                        <span className="text-2xl font-black text-slate-900 dark:text-white font-headline leading-tight">
                            {totalCount}
                        </span>
                        <span className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 leading-none mt-0.5">
                            Tasks
                        </span>
                    </div>
                </div>

                {/* Interactive Legend with values */}
                <div className="flex-1 space-y-2 text-xs">
                    {STATUS_CONFIGS.map((status) => {
                        const isSelected = selectedStatus === status.key;
                        const count = countsMap[status.key] || 0;

                        return (
                            <button
                                key={status.key}
                                type="button"
                                onClick={() => handleRowClick(status.key)}
                                className={`w-full flex items-center justify-between p-1 rounded-lg transition-all text-left cursor-pointer ${
                                    isSelected
                                        ? 'bg-blue-50 dark:bg-blue-950/40 ring-1 ring-blue-500/30'
                                        : 'hover:bg-slate-50 dark:hover:bg-slate-800/60'
                                }`}
                                title={`Filter by ${status.label}`}
                            >
                                <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300 font-medium">
                                    <span className={`w-2.5 h-2.5 rounded-full ${status.bgClass} flex-shrink-0`} />
                                    <span className={isSelected ? 'font-bold text-blue-700 dark:text-blue-300' : ''}>
                                        {status.label}
                                    </span>
                                </div>
                                <span className={`font-bold font-headline ml-2 ${
                                    isSelected ? 'text-blue-700 dark:text-blue-300' : 'text-slate-900 dark:text-white'
                                }`}>
                                    {count}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
